import { ApiErrorCode, parseApiError } from '@ooh/contracts';
import type { TFunction } from 'i18next';
import { toast } from '@ooh/ui/components/sonner';
import type { QuickAddOfferPickerConfirmCreationFailedResult } from '../quick-add-offer-picker';

/**
 * Maps a failure from the "creating" confirm path (`mode: 'creating'`,
 * before any offer exists) to `{status: 'creation-failed'}` for the two
 * causes with no field to blame: a 403 (e.g. a sales-department caller
 * lacking `offer.assign_other_salesperson`), or a 422
 * `CAMPAIGN_NUMBER_GENERATION_EXHAUSTED` (PR #1128 review — previously
 * unhandled here, unlike its sibling `CLIENT_ACRONYM_TAKEN` in
 * ClientDialog). Each shows its own dedicated toast. Returns `null` for
 * every other error so the caller can apply its own handling
 * (`useQuickAddPipeline` wraps it with failure context and rethrows;
 * `useCalendarOfferPicker` rethrows plain so its outer catch's
 * `recover-forbidden` handling applies).
 *
 * Shared by `useCalendarOfferPicker` (apps/web) and `useQuickAddPipeline`.
 * See `build-create-offer-payload.ts` for the "creating" payload mapping they also share.
 */
export function mapOfferCreationForbiddenError(
  error: unknown,
  t: TFunction,
): QuickAddOfferPickerConfirmCreationFailedResult | null {
  const parsed = parseApiError(error);
  if (parsed.errorCode === ApiErrorCode.CAMPAIGN_NUMBER_GENERATION_EXHAUSTED) {
    toast.error(t('quickAdd.errors.campaignNumberExhausted'));
    return { status: 'creation-failed' };
  }
  if (parsed.statusCode !== 403) {
    return null;
  }

  toast.error(t('quickAdd.errors.creationForbidden'));
  return { status: 'creation-failed' };
}
